import React, { useEffect, useRef, useState } from 'react';
import {
  triggerConfetti,
  triggerFireworks,
  triggerStarShower,
  triggerSchoolPride,
  triggerCrazyParty,
} from '../../utils/confetti';
import type { CelebrationMode } from '../../utils/confetti';

export { CelebrationMode };

interface FallingEmoji {
  id: number;
  emoji: string;
  left: number;
  duration: number;
  delay: number;
  size: number;
  rotation: number;
}

interface Props {
  mode: CelebrationMode;
  triggeredBy?: string;
  onDone: () => void;
}

const RETRO_EMOJIS = ['🎉', '🎊', '🚀', '⭐', '🏆', '💥', '🎯', '🔥', '✨', '🎸', '🥳', '🎆'];

const CELEBRATION_CONFIG: Record<CelebrationMode, {
  label: string;
  banner: string;
  emoji: string;
  bannerBg: string;
  emojiRain: boolean;
  nyanCat: boolean;
  shake: boolean;
  duration: number;
}> = {
  confetti: {
    label: 'Confetti!',
    banner: '🎉 Great Retro! 🎉',
    emoji: '🎉',
    bannerBg: 'from-purple-600 to-violet-500',
    emojiRain: false,
    nyanCat: false,
    shake: false,
    duration: 3500,
  },
  fireworks: {
    label: 'Fireworks!',
    banner: '🎆 Amazing! 🎆',
    emoji: '🎆',
    bannerBg: 'from-orange-500 to-red-500',
    emojiRain: false,
    nyanCat: false,
    shake: false,
    duration: 4000,
  },
  stars: {
    label: 'Star Shower!',
    banner: '⭐ Super Retro! ⭐',
    emoji: '⭐',
    bannerBg: 'from-yellow-400 to-amber-500',
    emojiRain: false,
    nyanCat: false,
    shake: false,
    duration: 3500,
  },
  school: {
    label: 'Team Spirit!',
    banner: '🏆 Team Win! 🏆',
    emoji: '🏆',
    bannerBg: 'from-violet-600 to-amber-500',
    emojiRain: false,
    nyanCat: true,
    shake: false,
    duration: 3000,
  },
  crazy: {
    label: 'CRAZY PARTY!',
    banner: '🤪 CRAZY PARTY! 🤪',
    emoji: '🤪',
    bannerBg: 'from-pink-500 via-yellow-400 to-cyan-400',
    emojiRain: true,
    nyanCat: true,
    shake: true,
    duration: 5000,
  },
};

const CelebrationEffect: React.FC<Props> = ({ mode, triggeredBy, onDone }) => {
  const config = CELEBRATION_CONFIG[mode];
  const [visible, setVisible] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [fallingEmojis, setFallingEmojis] = useState<FallingEmoji[]>([]);
  const [nyanPos, setNyanPos] = useState(-160);
  const [shake, setShake] = useState(false);
  const nyanRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;

    // Trigger confetti based on mode
    switch (mode) {
      case 'confetti': triggerConfetti({ particleCount: 180 }); break;
      case 'fireworks': triggerFireworks(); break;
      case 'stars': triggerStarShower(); break;
      case 'school': triggerSchoolPride(); break;
      case 'crazy': triggerCrazyParty(); break;
    }

    // Show banner with slight delay
    setTimeout(() => setShowBanner(true), 200);

    // Emoji rain
    if (config.emojiRain) {
      const emojis: FallingEmoji[] = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        emoji: RETRO_EMOJIS[Math.floor(Math.random() * RETRO_EMOJIS.length)],
        left: Math.random() * 100,
        duration: 2 + Math.random() * 2,
        delay: Math.random() * 2,
        size: 20 + Math.random() * 28,
        rotation: Math.random() * 360,
      }));
      setFallingEmojis(emojis);
    }

    // Screen shake
    if (config.shake) {
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }

    // Nyan cat
    if (config.nyanCat) {
      let pos = -160;
      nyanRef.current = setInterval(() => {
        pos += 8;
        setNyanPos(pos);
        if (pos > window.innerWidth + 160) {
          if (nyanRef.current) clearInterval(nyanRef.current);
        }
      }, 16);
    }

    // Cleanup
    const timer = setTimeout(() => {
      setVisible(false);
      setShowBanner(false);
      setFallingEmojis([]);
      doneRef.current = true;
      onDone();
    }, config.duration);

    return () => {
      clearTimeout(timer);
      if (nyanRef.current) clearInterval(nyanRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <>
      {/* Screen shake wrapper */}
      {shake && (
        <style>{`
          @keyframes screenShake {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            10% { transform: translate(-4px, -2px) rotate(-1deg); }
            20% { transform: translate(4px, 2px) rotate(1deg); }
            30% { transform: translate(-4px, 2px) rotate(0deg); }
            40% { transform: translate(4px, -2px) rotate(1deg); }
            50% { transform: translate(-2px, 4px) rotate(-1deg); }
            60% { transform: translate(2px, -4px) rotate(0deg); }
            70% { transform: translate(-4px, 2px) rotate(-1deg); }
            80% { transform: translate(4px, 2px) rotate(1deg); }
            90% { transform: translate(-2px, -2px) rotate(0deg); }
          }
          body { animation: screenShake 0.6s ease-in-out; }
        `}</style>
      )}

      {/* Emoji rain */}
      {fallingEmojis.length > 0 && (
        <>
          <style>{`
            @keyframes emojifall {
              0% { transform: translateY(-60px) rotate(0deg); opacity: 1; }
              100% { transform: translateY(110vh) rotate(var(--rot)); opacity: 0.7; }
            }
          `}</style>
          <div className="fixed inset-0 pointer-events-none z-[9990] overflow-hidden">
            {fallingEmojis.map((e) => (
              <span
                key={e.id}
                style={{
                  position: 'absolute',
                  left: `${e.left}%`,
                  top: '-60px',
                  fontSize: `${e.size}px`,
                  animation: `emojifall ${e.duration}s ease-in ${e.delay}s forwards`,
                  ['--rot' as string]: `${e.rotation}deg`,
                  userSelect: 'none',
                }}
              >
                {e.emoji}
              </span>
            ))}
          </div>
        </>
      )}

      {/* Nyan Cat */}
      {config.nyanCat && (
        <div
          className="fixed pointer-events-none z-[9991]"
          style={{ bottom: 60, left: nyanPos, whiteSpace: 'nowrap' }}
        >
          <span style={{ fontSize: 40 }}>🌈🐱</span>
          <span style={{ fontSize: 20, marginLeft: 4 }}>✨✨✨</span>
        </div>
      )}

      {/* Celebration Banner */}
      {showBanner && (
        <>
          <style>{`
            @keyframes bannerIn {
              0% { transform: translateY(-80px) scale(0.8); opacity: 0; }
              60% { transform: translateY(8px) scale(1.06); opacity: 1; }
              80% { transform: translateY(-4px) scale(0.98); }
              100% { transform: translateY(0) scale(1); opacity: 1; }
            }
            @keyframes bannerOut {
              0% { transform: translateY(0) scale(1); opacity: 1; }
              100% { transform: translateY(-80px) scale(0.8); opacity: 0; }
            }
            @keyframes pulseText {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.04); }
            }
          `}</style>
          <div
            className="fixed top-16 left-1/2 pointer-events-none z-[9998]"
            style={{ transform: 'translateX(-50%)', animation: 'bannerIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
          >
            <div className={`bg-gradient-to-r ${config.bannerBg} text-white px-8 py-4 rounded-2xl shadow-2xl`}>
              <div
                className="text-2xl font-black text-center tracking-wide"
                style={{ animation: 'pulseText 0.8s ease-in-out infinite', textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
              >
                {config.banner}
              </div>
              {triggeredBy && (
                <p className="text-sm text-white/80 text-center mt-1 font-medium">
                  {triggeredBy} started a celebration!
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CelebrationEffect;
